import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusMonitor } from '@angular/cdk/a11y';
import mixinDisabled from '../../mixins/disabled';
import mixinTabIndex from '../../mixins/tabindex';
import { CheckUI, Subtract } from './../icon/icon-map';

let nextUniqueId = 0;

export type PhxCheckboxStates = 'off' | 'on' | 'indeterminate';
export enum PHX_CHECKBOX_STATES {
  OFF = 'off',
  ON = 'on',
  INDETERMINATE = 'indeterminate',
}

const baseCheckboxMixin = mixinTabIndex(
  mixinDisabled(
    class {
      constructor(public _elementRef: ElementRef) {}
    }
  )
);

@Component({
  selector: 'phx-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  host: {
    class: 'phx-checkbox',
    '[class.phx-checkbox-checked]': 'phxChecked',
    '[class.phx-checkbox-unchecked]': '!phxChecked && !phxIndeterminate',
    '[class.phx-checkbox-indeterminate]': 'phxIndeterminate && !phxChecked',
    '[class.phx-checkbox-inherit-width]': 'phxInheritWidth',
    '[class.phx-checkbox-disabled]': 'disabled',
    '[attr.tabindex]': 'null',
    '(click)': '$event.stopPropagation()',
  },
  inputs: ['disabled', 'tabIndex'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxCheckboxComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PhxCheckboxComponent extends baseCheckboxMixin implements AfterViewInit, OnDestroy, OnChanges, ControlValueAccessor {
  @Input() public phxIndeterminate: boolean = false;
  @Input() public phxId: number | string;
  @Input() public phxRequired: boolean = false;
  @Input() public phxValue: string | null = null;
  @Input() public phxName: string | null = null;
  @Input() public phxUseBoolean: boolean = false;
  @Input() public phxInheritWidth: boolean = false;
  @Input()
  get phxChecked(): boolean {
    return this.state === 'on';
  }
  set phxChecked(value: boolean) {
    const checked = coerceBooleanProperty(value);
    if (checked != this.phxChecked) {
      this.state = checked ? 'on' : 'off';
      this.cdr.markForCheck();
    }
  }

  @Output() public readonly phxIndeterminateChange = new EventEmitter<boolean>();
  @Output() public readonly phxCheckedChange = new EventEmitter<boolean>();
  @ViewChild('input') private inputElement: ElementRef<HTMLInputElement>;
  public CheckUIIcon: string = CheckUI;
  public SubtractIcon: string = Subtract;
  private readonly generatedId: number = nextUniqueId++;
  private onChange = (val) => {};
  private onTouched = () => {};
  private state: PhxCheckboxStates = 'off';

  public get inputId() {
    return `phx-checkbox-input-${this.phxId || this.generatedId}`;
  }

  constructor(private cdr: ChangeDetectorRef, el: ElementRef, private focusMonitor: FocusMonitor) {
    super(el);
  }

  ngAfterViewInit(): void {
    this.focusMonitor.monitor(this._elementRef, true).subscribe((focusOrigin) => {
      if (!focusOrigin) {
        Promise.resolve().then(() => {
          this.onTouched();
          this.cdr.detectChanges();
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.focusMonitor.stopMonitoring(this._elementRef);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { phxChecked, phxIndeterminate } = changes;

    if (phxChecked || phxIndeterminate) {
      if (phxChecked?.currentValue) this.writeValue('on');
      else if (phxIndeterminate?.currentValue) this.writeValue('indeterminate');
      else this.writeValue('off');
      this.onChange(this.getState());
      this.cdr.detectChanges();
    }
  }

  writeValue(value: PhxCheckboxStates | boolean) {
    if (this.phxUseBoolean) {
      if (value) {
        this.phxChecked = true;
        this.phxIndeterminate = false;
      } else {
        this.phxChecked = false;
        this.phxIndeterminate = false;
      }
    } else {
      if (value === 'off') {
        this.phxChecked = false;
        this.phxIndeterminate = false;
      } else if (value === 'on') {
        this.phxChecked = true;
        this.phxIndeterminate = false;
      } else if (value === 'indeterminate') {
        this.phxIndeterminate = true;
        this.phxChecked = false;
      }
    }
    this.cdr.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
    this.cdr.detectChanges();
  }

  public onChangeEvent(event) {
    if (!this.disabled) {
      this.phxChecked = !this.phxChecked;

      if (this.phxIndeterminate) {
        this.phxIndeterminate = false;
        this.phxIndeterminateChange.emit(this.phxIndeterminate);
      }

      this.phxCheckedChange.emit(this.phxChecked);

      this.onChange(this.getState());
    }
  }

  getState(): PhxCheckboxStates | boolean {
    if (this.phxUseBoolean) {
      return this.phxChecked;
    } else {
      if (this.phxChecked) return 'on';
      else if (this.phxIndeterminate) return 'indeterminate';
      else return 'off';
    }
  }
}
